import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { initialAuthLink } from './services/supabase';
import Layout from './components/Layout';
import LoginPage from './features/auth/LoginPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import SetPasswordPage from './features/auth/SetPasswordPage';
import HomePage from './features/home/HomePage';
import PeoplePage from './features/browse/PeoplePage';
import PersonDetailPage from './features/browse/PersonDetailPage';
import TasksPage from './features/browse/TasksPage';
import OutcomesPage from './features/outcomes/OutcomesPage';
import TaskDetailPage from './features/browse/TaskDetailPage';
import ContainerListPage from './features/containers/ContainerListPage';
import ContainerDetailPage from './features/containers/ContainerDetailPage';
import NoteDetailPage from './features/notes/NoteDetailPage';
import ArchivePage from './features/archive/ArchivePage';
import DigestPage from './features/digest/DigestPage';
import BrainDumpPage from './features/capture/BrainDumpPage';
import SettingsPage from './features/settings/SettingsPage';
import ImportPage from './features/import/ImportPage';
import CalendarPage from './features/calendar/CalendarPage';
import SearchPage from './pages/SearchPage';
import PatternsPage from './pages/PatternsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import SlackCallbackPage from './pages/SlackCallbackPage';
import HowItWorksPage from './features/guide/HowItWorksPage';
import FeedbackListPage from './features/feedback/FeedbackListPage';

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const navigate = useNavigate();
  // An invite or reset link can land on any path (a Site URL entered with a trailing
  // slash, say), so send it to the set-password page first. An unspent token stays in
  // the address bar, so a reload (Safari reloads tabs it put to sleep) still has it;
  // any other link data (tokens or errors) leaves it.
  const [linkPending, setLinkPending] = useState(initialAuthLink !== null);

  useEffect(() => {
    if (!linkPending || !initialAuthLink) return;
    setLinkPending(false);
    if (initialAuthLink.kind === 'token_hash') {
      const query = new URLSearchParams({ token_hash: initialAuthLink.tokenHash, type: initialAuthLink.type });
      navigate(`/set-password?${query}`, { replace: true });
    } else {
      navigate('/set-password', { replace: true });
    }
  }, [linkPending, navigate]);

  if (linkPending) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="people/:id" element={<PersonDetailPage />} />
        <Route path="projects" element={<ContainerListPage kind="project" />} />
        <Route path="projects/:id" element={<ContainerDetailPage kind="project" />} />
        <Route path="areas" element={<ContainerListPage kind="area" />} />
        <Route path="areas/:id" element={<ContainerDetailPage kind="area" />} />
        <Route path="resources" element={<ContainerListPage kind="resource" />} />
        <Route path="resources/:id" element={<ContainerDetailPage kind="resource" />} />
        <Route path="notes/:id" element={<NoteDetailPage />} />
        <Route path="archive" element={<ArchivePage />} />
        {/* Ideas became notes in the Ideas resource. */}
        <Route path="ideas" element={<Navigate to="/resources" replace />} />
        <Route path="outcomes" element={<OutcomesPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="tasks/:id" element={<TaskDetailPage />} />
        <Route path="digest" element={<DigestPage />} />
        <Route path="brain-dump" element={<BrainDumpPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="patterns" element={<PatternsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="integrations/slack/callback" element={<SlackCallbackPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="how-it-works" element={<HowItWorksPage />} />
        <Route path="feedback" element={<FeedbackListPage />} />
      </Route>
    </Routes>
  );
}
